import fetchData from './fetchData';

export interface ConversionTimeData {
  email: string;
  first_signup: string;
  first_order: string;
  days_to_convert: number;
  product_code: string;
}

export interface ConversionRateData {
  total_emails: number;
  emails_with_orders: number;
  total_orders: number;
  converted_emails: number;
  conversion_rate: number;
  total_revenue: number;
  emails_with_revenue: number;
  orders_with_revenue: number;
  avg_revenue_per_signup: number;
  avg_revenue_per_customer: number;
  avg_revenue_per_order: number;
  revenue_conversion_rate: number;
  expected_revenue: number;
  expected_avg_per_signup: number;
  expected_avg_per_customer: number;
  total_cpa: number;
  cpa_per_signup: number;
  cpa_per_customer: number;
}

export interface ConversionDistributionData {
  time_bucket: string;
  count: number;
  percentage: number;
}

export interface ProductConversionData {
  product_code: string;
  product_name: string;
  converted_emails: number;
  total_orders: number;
  avg_days_to_convert: number;
  conversion_rate: number;
  total_revenue: number;
  avg_revenue_per_order: number;
  total_cpa: number;
  avg_cpa_per_order: number;
}

export interface ConversionTimelineData {
  month: string;
  new_signups: number;
  conversions: number;
  revenue: number;
  monthly_conversion_rate: number;
}

export interface SignupCohortData {
  time_bucket: string;
  signup_count: number;
  percentage: number;
}

export interface ConversionDetailData {
  email: string;
  signup_date: string;
  first_order_date: string | null;
  days_to_convert: number;
  product_code: string;
  product_name: string;
  source?: string;
  affiliate?: string;
  client?: string;
  status?: string;
}

export interface CpaTimelineData {
  month: string;
  new_signups: number;
  total_cpa: number;
  cpa_per_signup: number;
}

export interface DomainRevenueData {
  domain: string;
  total_emails: number;
  converted_emails: number;
  total_orders: number;
  conversion_rate: number;
  total_revenue: number;
  avg_revenue_per_email: number;
  avg_revenue_per_customer: number;
}

export const fetchConversionTime = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionTimeData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionTimeData>(
    '/api/anfo-statistics/conversion-time',
    params,
    true,
  ) as Promise<ConversionTimeData[]>;
};

export const fetchConversionRate = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionRateData> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionRateData>(
    '/api/anfo-statistics/conversion-rate',
    params,
    false,
  ) as Promise<ConversionRateData>;
};

export const fetchConversionDistribution = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionDistributionData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionDistributionData>(
    '/api/anfo-statistics/conversion-distribution',
    params,
    true,
  ) as Promise<ConversionDistributionData[]>;
};

export const fetchProductConversion = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ProductConversionData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ProductConversionData>(
    '/api/anfo-statistics/product-conversion',
    params,
    true,
  ) as Promise<ProductConversionData[]>;
};

export const fetchConversionTimeline = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionTimelineData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionTimelineData>(
    '/api/anfo-statistics/conversion-timeline',
    params,
    true,
  ) as Promise<ConversionTimelineData[]>;
};

export const fetchSignupCohortAnalysis = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<SignupCohortData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<SignupCohortData>(
    '/api/anfo-statistics/signup-cohort-analysis',
    params,
    true,
  ) as Promise<SignupCohortData[]>;
};

export const fetchConversionDetails = async (
  timeBucket: string,
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionDetailData[]> => {
  const params: any = { timeBucket };
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionDetailData>(
    '/api/anfo-statistics/conversion-details',
    params,
    true,
  ) as Promise<ConversionDetailData[]>;
};

export const fetchProductConversionDetails = async (
  productCode: string,
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<ConversionDetailData[]> => {
  const params: any = { productCode };
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<ConversionDetailData>(
    '/api/anfo-statistics/product-conversion-details',
    params,
    true,
  ) as Promise<ConversionDetailData[]>;
};

export const fetchCpaTimeline = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<CpaTimelineData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<CpaTimelineData>('/api/anfo-statistics/cpa-timeline', params, true) as Promise<
    CpaTimelineData[]
  >;
};

export const fetchCpaTimelineWindowed = async (
  signupStartDate?: string,
  signupEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  days = 30,
  sources?: string[],
): Promise<CpaTimelineData[]> => {
  const params: any = { days };
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<CpaTimelineData>(
    '/api/anfo-statistics/cpa-timeline-windowed',
    params,
    true,
  ) as Promise<CpaTimelineData[]>;
};

export const fetchDomainRevenue = async (
  signupStartDate?: string,
  signupEndDate?: string,
  orderStartDate?: string,
  orderEndDate?: string,
  clients?: string[],
  aboStatuses?: string[],
  sources?: string[],
): Promise<DomainRevenueData[]> => {
  const params: any = {};
  if (signupStartDate) params.signupStartDate = signupStartDate;
  if (signupEndDate) params.signupEndDate = signupEndDate;
  if (orderStartDate) params.orderStartDate = orderStartDate;
  if (orderEndDate) params.orderEndDate = orderEndDate;
  if (clients && clients.length > 0) params.clients = clients.join(',');
  if (aboStatuses && aboStatuses.length > 0) params.aboStatuses = aboStatuses.join(',');
  if (sources && sources.length > 0) params.sources = sources.join(',');
  return fetchData<DomainRevenueData>(
    '/api/anfo-statistics/domain-revenue',
    params,
    true,
  ) as Promise<DomainRevenueData[]>;
};

// Helper function to compute summary statistics from conversion time data
export const computeConversionTimeSummary = (
  data: ConversionTimeData[],
): {
  averageDays: number;
  medianDays: number;
  minDays: number;
  maxDays: number;
} => {
  if (!data.length) {
    return {
      averageDays: 0,
      medianDays: 0,
      minDays: 0,
      maxDays: 0,
    };
  }

  const sortedDays = data.map((item) => item.days_to_convert).sort((a, b) => a - b);
  const sum = sortedDays.reduce((acc, val) => acc + val, 0);

  return {
    averageDays: Math.round((sum / sortedDays.length) * 10) / 10,
    medianDays:
      sortedDays.length % 2 === 0
        ? (sortedDays[sortedDays.length / 2 - 1] + sortedDays[sortedDays.length / 2]) / 2
        : sortedDays[Math.floor(sortedDays.length / 2)],
    minDays: sortedDays[0],
    maxDays: sortedDays[sortedDays.length - 1],
  };
};
